import i18next from "i18next"

/**
 * 客户授权（合规）访问管控 —— 超管后台白名单
 *
 * 场景：超管后台（`window.isFromAdmin === true`，即非 Shopify admin app 环境）代客进入 APP 时，
 * 若客户尚未授权（进入链接带 `access_scope=limited`），只开放白名单里的页面。
 * 商家自己在 Shopify 后台内嵌使用完全不受影响 —— 所有判定的第一个条件都是超管后台环境。
 *
 * 三种状态：
 * - 商家环境             → 全量路由，与改动前一致
 * - 超管后台 + 客户已授权 → 全量路由，同样不受限
 * - 超管后台 + 客户未授权 → 只保留 ADMIN_SCOPED_ROUTES 白名单，其余重定向到 FALLBACK
 *
 * ---
 * 扩展白名单：在 ADMIN_SCOPED_ROUTES 加一项即可同时生效于「路由匹配 + 顶部菜单 + Tracking page 左侧菜单」，
 * 另外还需要在 App.tsx 的 renderAdminScopedRoutes() 里补上对应的 <Route>（那里是真正的页面元素）。
 */

// ------------------------------------------------------------------
// 客户授权状态：来自进入链接上的 access_scope
// ------------------------------------------------------------------

/** 超管后台代客进入时，链接带这个值代表客户未授权 */
export const ACCESS_SCOPE_LIMITED = "limited"

const ACCESS_SCOPE_PARAM = "access_scope"
const ACCESS_SCOPE_STORAGE_KEY = "pp:access-scope"

/**
 * 把 URL 上的 access_scope 落到 sessionStorage
 *
 * SPA 内部跳转会把 query 丢掉，所以模块加载时（页面刚进来、URL 还完整）先捕获一次，
 * 之后每次读取再试一次，兼容带参刷新。
 */
const captureAccessScopeFromUrl = (): void => {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get(ACCESS_SCOPE_PARAM)

    if (fromUrl) {
      window.sessionStorage.setItem(ACCESS_SCOPE_STORAGE_KEY, fromUrl)
    }
  } catch (e) {
    console.debug("[customerAuthGate] 写入 access_scope 失败", e)
  }
}

captureAccessScopeFromUrl()

/** 当前会话的 access_scope，URL 上没有就用 sessionStorage 里记下的 */
export const getAccessScope = (): string => {
  captureAccessScopeFromUrl()

  try {
    return window.sessionStorage.getItem(ACCESS_SCOPE_STORAGE_KEY) || ""
  } catch (e) {
    console.debug("[customerAuthGate] 读取 access_scope 失败", e)

    return ""
  }
}

/** 客户是否未授权（进入链接带 access_scope=limited） */
export const isLimitedAccessScope = (): boolean => getAccessScope() === ACCESS_SCOPE_LIMITED

export type AdminScopedRoute = {
  /** 路由路径，与 App.tsx 里的完整 pathname 对应 */
  path: string
  /** 是否连同子路由一起放行，如 /orders 下的 detail、抽屉、update-status 等 */
  includeChildren?: boolean
  /** includeChildren 放行范围内要单独排除的子路径（连同它们自己的子路由一起排除） */
  excludePaths?: string[]
  /** 对应的顶部菜单项 id（GlobalMenuStore.tabs 的 id）；不填则该页面不出现在顶部菜单 */
  menuTabId?: string
  /** 对应的 Tracking page 左侧子菜单 item id；不填则不出现在左侧列表 */
  trackingPageTabId?: string
}

/** 超管后台代客进入、且客户未授权时开放的页面白名单 */
export const ADMIN_SCOPED_ROUTES: AdminScopedRoute[] = [
  {
    path: "/orders",
    includeChildren: true,
    // Carrier matching 是商家侧的运输商匹配配置，Manually update status 是批量改状态，代客场景都不开放
    excludePaths: ["/orders/carrier-matching", "/orders/update-status"],
    menuTabId: "/orders",
  },
  {
    // Tracking page 根路由，渲染的就是 Appearance，保留它菜单入口才点得进去
    path: "/tracking-page",
    menuTabId: "/tracking-page",
  },
  {
    path: "/tracking-page/appearance",
    trackingPageTabId: "appearance",
  },
  {
    path: "/tracking-page/CSS&HTML",
    trackingPageTabId: "CSS&HTML",
  },
]

/** 超管后台进入后的默认落地页，白名单之外的路径一律重定向到这里 */
export const ADMIN_SCOPED_FALLBACK_PATH = "/orders"

const trimTrailingSlash = (pathname: string): string => (
  pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname
)

/** 路径是否在超管后台白名单内 */
export const isAdminScopedPath = (pathname: string): boolean => {
  const target = trimTrailingSlash(pathname)

  return ADMIN_SCOPED_ROUTES.some(({ path, includeChildren, excludePaths }) => {
    const matched = includeChildren ? target === path || target.startsWith(`${path}/`) : target === path

    if (!matched) return false

    return !(excludePaths || []).some(excluded => target === excluded || target.startsWith(`${excluded}/`))
  })
}

/** 白名单里配置了顶部菜单的 tab id 集合 */
export const adminScopedMenuTabIds = (): Set<string> => new Set(
  ADMIN_SCOPED_ROUTES.map(item => item.menuTabId)
    .filter((id): id is string => !!id),
)

/** 白名单里配置了 Tracking page 左侧菜单的 item id → 实际跳转路径 */
export const adminScopedTrackingPageTabs = (): Map<string, string> => new Map(
  ADMIN_SCOPED_ROUTES
    .filter(item => !!item.trackingPageTabId)
    .map(item => [item.trackingPageTabId as string, item.path]),
)

// ------------------------------------------------------------------
// Orders 页在白名单模式下的收窄配置
// ------------------------------------------------------------------

/**
 * 固定展示的表格列，数组顺序即列顺序；白名单模式下列设置入口隐藏，不可更改
 * id 对应 OrdersTable 的 columnConfig
 */
export const ADMIN_SCOPED_ORDER_COLUMNS = [
  "trackingNumber",
  "shopifyCarrier",
  "courier",
  "lastCheckPoint",
  "transit",
  "orderDate",
  "status",
  "substatus",
  "lastCheckpointTime",
  "residenceTime",
  "lastMileTrackingNumber",
  "lastMileCarrier",
]

/** 保留的排序字段，对应 getSortButtonChoices() 里 value 的前半段 */
export const ADMIN_SCOPED_ORDER_SORT_FIELDS = ["orderedAt", "transit", "residence", "lastCheckpointTime"]

/** 保留的筛选项，值取自 enums/shipment-filter-key 的 ShipmentFilterKey */
export const ADMIN_SCOPED_ORDER_FILTER_KEYS = ["Status", "Courier", "OrderDate"]

/**
 * 白名单模式下仍需请求统计的筛选项字段，对应 Orders 页 getAllFilterCount() 里的 field
 * 与 ADMIN_SCOPED_ORDER_FILTER_KEYS 一一对应（Order date 用本地选项，不发请求）
 */
export const ADMIN_SCOPED_ORDER_FILTER_FIELDS = ["status", "courier"]

/**
 * 订单详情右侧 Shipment information 里保留的字段
 * 物流商 / 最后一公里的 logo、名称、单号是固定展示的，不在这个列表里
 */
export const ADMIN_SCOPED_SHIPMENT_INFO_FIELDS = [
  "orderDate",
  "shipmentStatus",
  "shipmentSubStatus",
  "processingTime",
  "pickupTime",
  "transitTime",
  "orderToDeliveryTime",
  "residenceTime",
]

export const ORDER_WORD_MASK = "ORD"

type OrderWordRule = {
  /**
   * 词干（小写）。匹配时会连同后面的字母一起吃掉，用来覆盖各语言的词形变化，
   * 例如 de 的 bestell → Bestellungen / Bestellnummer 都会整词替换成 ORD。
   * 仅适用于有词边界的语言。
   */
  stems?: string[]
  /**
   * 整词字面量，按原样替换。用于 CJK / 泰语这类没有词边界的语言，
   * 不能用词干吃字母的方式，否则会把后面的字一起吞掉（订单日期 → ORD）。
   */
  literals?: string[]
}

/**
 * 各语言里「订单」一词的写法
 *
 * 取自各语言包 menu:orders / orders:OrderDate / orders:NoOrdersYet 等实际译文；
 * 新增语言或发现漏网词形时，在这里补一项即可。
 */
const ORDER_WORDS: Record<string, OrderWordRule> = {
  en: { stems: ["order"] },
  cs: { stems: ["objednáv", "objednav"] },
  da: { stems: ["ordre"] },
  de: { stems: ["bestell", "auftrag"] },
  es: { stems: ["pedido", "orden", "órden"] },
  fi: { stems: ["tilau"] },
  fr: { stems: ["commande"] },
  it: { stems: ["ordin"] },
  ja: { literals: ["ご注文", "注文"] },
  ko: { literals: ["주문"] },
  nb: { stems: ["ordre", "bestilling"] },
  nl: { stems: ["bestel"] },
  no: { stems: ["ordre", "bestilling"] },
  pl: { stems: ["zamówie", "zamowie"] },
  pt: { stems: ["pedido", "encomenda"] },
  sv: { stems: ["beställning", "order"] },
  th: { literals: ["คําสั่งซื้อ", "คำสั่งซื้อ", "สั่งซื้อ", "ใบ สั่ง"] },
  tr: { stems: ["sipariş", "siparis"] },
  zh: { literals: ["订单", "訂單", "下单", "下單"] },
}

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

/** i18n 语言码 → 词表 key，先精确匹配再退到主语言（pt-BR → pt、zh-HK → zh） */
const resolveOrderWordRule = (language: string): OrderWordRule => {
  const lang = (language || "en").toLowerCase()

  return ORDER_WORDS[lang] || ORDER_WORDS[lang.split("-")[0]] || {}
}

/**
 * 白名单模式下把界面上的「订单」字样替换为 ORD
 *
 * 按当前语言的词表替换，并且始终叠加英文词表 —— 译文缺失时 i18next 会回落到英文默认值。
 */
export const maskOrderWord = (text: string, language = i18next.language): string => {
  const rule = resolveOrderWordRule(language)
  const stems = [...(rule.stems || []), ...(ORDER_WORDS.en.stems || [])]
  const literals = rule.literals || []

  let result = text

  // 无词边界的语言：整词字面量替换，长词优先，避免短词先命中把长词拆碎
  for (const literal of [...literals].sort((a, b) => b.length - a.length)) {
    result = result.split(literal)
      .join(ORDER_WORD_MASK)
  }

  if (!stems.length) return result

  const stemRe = new RegExp(`(?:${stems.map(escapeRegExp).join("|")})\\p{L}*`, "giu")

  // 不用 lookbehind（Safari 16.4 以下会直接抛正则语法错），改在回调里判断前一个字符是不是字母，
  // 这样 border / reorder 这类把词干包在中间的单词不会被误伤
  return result.replace(stemRe, (matched, offset: number, full: string) => {
    const prev = full[offset - 1]

    return prev && /\p{L}/u.test(prev) ? matched : ORDER_WORD_MASK
  })
}

/**
 * 包装 i18next 的 t：白名单模式下把返回的字符串里的 Order / Orders 换成 ORD
 * enabled 为 false 时原样返回，不产生任何开销
 */
export const maskOrderWordInT = <T extends (...args: any[]) => any>(t: T, enabled: boolean): T => {
  if (!enabled) return t

  return ((...args: Parameters<T>) => {
    const value = t(...args)

    return typeof value === "string" ? maskOrderWord(value) : value
  }) as T
}
